/* istanbul ignore file */
// FIXME: Add tests

import { useCallback, useEffect, useState } from 'react';
import { useEventWebsiteGuestQuery, EventWebsiteGuestQuery, DesignLayoutType, EventDesignPurpose } from '@graphql/generated';
import { useImmer } from 'use-immer';
import { isInIframe } from '@shared/utils/isInIframe';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { shouldUseEventDesignDraft, useEventDesignTheming, useReloadForLegacyGuestsite } from './GuestSite.utils';
import { useEventPageRouting } from './GuestSite.routes';
import { OnPreviewListenerUpdate } from './PreviewListener';
import { useIframeScreen, UseIframeScreenArgs } from './useIframeScreen';
import globalWindow from '@shared/core/globals';
import { useGuestSiteTelemetry } from '../../GuestSite.telemetry';
import type { StaticContext } from '@react-router';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useFeatureFlagsContext } from '@shared/core/featureFlags';

interface GuestSiteArgs
  extends Readonly<{
    eventHandle: string;
    staticContext: (StaticContext & { guestSiteEventHandleToVerify?: string }) | undefined;
  }> {}

const isPreviewing = isInIframe();

export const useGuestSiteController = (args: GuestSiteArgs) => {
  const { eventHandle, staticContext } = args;
  const [loadingTheme, setLoadingTheme] = useState(true);

  // FeatureFlag => wait for StyleApplicator https://github.com/joylifeinc/joy-web/pull/3214
  const featureFlagsContext = useFeatureFlagsContext();
  const styledApplicatorLoading = featureFlagsContext.getFeatureFlagValue('enableLoadingStyleApplicator') === 'true';
  const { data, loading, refetch } = useEventWebsiteGuestQuery({
    variables: { eventHandle, eventDesignPurpose: shouldUseEventDesignDraft() ? EventDesignPurpose.draft : EventDesignPurpose.live },
    batchMode: 'fast',
    ssr: true,
    fetchPolicy: 'cache-first'
  });

  const [{ eventPreview }, setState] = useImmer<{ eventPreview: EventWebsiteGuestQuery | undefined }>(() => ({
    eventPreview: data
  }));

  const { isAdmin } = useEventUserRole();

  const [showBannerReminderChanges, setShowBannerReminderChanges] = useState(false);

  const [showAnnouncement, setShowAnnouncement] = useState(false);

  // If previewing, use the clone. Else, use the original.
  const event = isPreviewing ? eventPreview?.eventByName : data?.eventByName;
  const layout = event?.eventDesign?.websiteLayout.layoutType || DesignLayoutType.aloha;
  const hasUnpublishedChanges = event?.eventDesign?.hasUnpublishedChanges;

  if (staticContext) {
    staticContext.guestSiteEventHandleToVerify = eventHandle;
  }

  const { pages = [] } = event || {};

  const eventPageRoutes = useEventPageRouting(eventHandle, pages);

  // Update JoyKit theme object with event design prferences
  const { theme, loadingFonts } = useEventDesignTheming({ eventDesign: event?.eventDesign });

  const telemetry = useGuestSiteTelemetry();
  const updateContext = telemetry.updateContext;

  useEffect(() => {
    if (event && layout) updateContext(prev => ({ ...prev, eventId: event.id, layout }));
  }, [event, layout, updateContext]);

  // Preview

  // Initialize preview data when query resolves
  useEffect(() => {
    if (isPreviewing) {
      setState(draft => {
        draft.eventPreview = data;
      });
    }
  }, [setState, data]);

  // Respond to event preview updates if previewing
  const handleOnPreviewUpdate = useEventCallback<OnPreviewListenerUpdate>(cb => {
    if (isPreviewing) {
      setState(draft => {
        const eventDraft = draft.eventPreview?.eventByName;
        if (eventDraft) {
          cb(eventDraft);
        }
      });
    }
  });

  const handleOnRefetchData = useEventCallback(() => {
    refetch();
  });

  const onScreenClick = useEventCallback<UseIframeScreenArgs['onClick']>(e => {
    globalWindow.open?.(`/${eventHandle}`, 'blank');
    telemetry.adminPreviewClick();
  });

  // Show/Hide BannerUnPublishedChanges
  useEffect(() => {
    if (event && !isPreviewing && isAdmin && hasUnpublishedChanges) {
      setShowBannerReminderChanges(true);
      telemetry.notPublishedBanner('Shown');
    } else {
      setShowBannerReminderChanges(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, layout, isAdmin, hasUnpublishedChanges]);

  const onDismissReminder = useCallback(() => {
    telemetry.notPublishedBanner('Dismissed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickReminder = useCallback(() => {
    telemetry.notPublishedBanner('Published');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Annoucement

  useEffect(() => {
    if (event?.info?.announcementBanner?.active && !isPreviewing) {
      setShowAnnouncement(true);
      telemetry.announcementBannerChange(event?.info?.announcementBanner?.title, 'Shown');
    } else {
      setShowAnnouncement(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  const onAnnouncementDismiss = useCallback((title: string) => {
    telemetry.announcementBannerChange(title, 'Dismissed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useIframeScreen({ onClick: onScreenClick });
  useReloadForLegacyGuestsite(event?.featureFlags, event?.website, event?.useLegacyGuestSite);
  const justFonts = useQueryParamHelper().getValue('justFonts');
  const rsvpPage = event?.pages?.find(page => page.pageSlug === 'rsvp');
  const graphicAccent = layout === 'brannan' ? event?.eventDesign?.accent ?? 'default' : undefined;
  const shouldRenderActionBar = !isAdmin && !isPreviewing;
  const loadingGuestSite = loading || (styledApplicatorLoading ? loadingTheme || loadingFonts : false);

  return {
    event,
    layout,
    loading: loadingGuestSite,
    hasUnpublishedChanges,
    isAdmin,
    isPreviewing,
    handleOnPreviewUpdate,
    handleOnRefetchData,
    eventPageRoutes,
    theme,
    justFonts,
    rsvpPage,
    graphicAccent,
    shouldRenderActionBar,
    loadingTheme,
    showBannerReminderChanges,
    setLoadingTheme,
    onDismissReminder,
    onClickReminder,
    showAnnouncement,
    onAnnouncementDismiss
  } as const;
};
